import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

import account from './account'
import claim from './claim'
import currencies from './currencies.js'
import dossier from './dossier'
import log from './log.js'
import uistrings from './uistrings'

import { handleApiCall } from '@/utils/handleApiCall'
import { cultureCheck } from '@/utils/cultureCheck'

Vue.use(Vuex)

export default new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  plugins: [createPersistedState({
    storage: window.sessionStorage
  })],
  state: {
    loggedIn: false,
    lastDossierRefresh: null,
    needsAcceptDisclaimer: false,
    internalUser: '',
    culture: cultureCheck(),
    countries: [],
    supportedFileTypes: [],
    postLoginRedirect: null
  },
  modules: {
    account,
    claim,
    currencies,
    dossier,
    log,
    uistrings
  },
  actions: {
    async downloadFile ({ commit }, data) {
      return await handleApiCall('downloadFile', '', 'checkLogin', data)
    },
    async uploadFiles ({ commit }, data) {
      return await handleApiCall('uploadFiles', '', '', data)
    },
    async uploadReceipts ({ commit }, data) {
      return await handleApiCall('uploadReceipts', '', '', data)
    },
    async postCreateNewPassword ({ commit }, data) {
      commit('UPDATE_DOSSIER', data)
    },
    async login ({ commit }, credentials) {
      commit('THROTTLE_DOSSIER_UPDATE')
      return await handleApiCall('login', 'UPDATE_DOSSIER', 'handleDossier', credentials)
    },
    async checkLogin ({ commit }) {
      return await handleApiCall('checkLogin', 'CHECK_LOGIN')
    },
    async refreshDossier ({ state, commit, dispatch }) {
      if (!state.lastDossierRefresh || new Date().getTime() - state.lastDossierRefresh > (1000 * 10)) {
        commit('THROTTLE_DOSSIER_UPDATE')
        handleApiCall('refreshDossier', '', 'handleDossier')
      }
    },
    async logout ({ commit }) {
      return await handleApiCall('logout', 'HANDLE_LOGOUT')
    },
    async internalLogin ({ commit }, credentials) {
      commit('THROTTLE_DOSSIER_UPDATE')
      return await handleApiCall('internalLogin', 'HANDLE_INTERNAL_LOGIN', 'handleDossier', credentials)
    },
    async acceptTerms ({ commit }) {
      return await handleApiCall('acceptTerms', 'HANDLE_ACCEPT_TERMS', 'refreshDossier')
    },
    async refreshSupportedFileTypes ({ commit, state }) {
      if (state.supportedFileTypes.length === 0) {
        await handleApiCall('getSupportedFileTypes', 'HANDLE_SUPPORTED_FILETYPES')
      }
    },
    setPostLoginRedirect ({ commit }, data) {
      commit('SET_POST_LOGIN_REDIRECT', data)
    },
    handleDossier ({ commit }, dossier) {
      if (dossier.Countries) {
        commit('HANDLE_COUNTRIES', dossier.Countries)
      }
      if (dossier.Claims) {
        this.dispatch('dossier/initializeClaims', dossier.Claims.Claim)
      }
      if (dossier.ManagedClaims) {
        this.dispatch('dossier/initializeManagedClaims', dossier.ManagedClaims.Claim)
      }
      if (dossier.Contact) {
        this.dispatch('account/initializeAccount', dossier.Contact)
      }
    }
  },
  mutations: {
    THROTTLE_DOSSIER_UPDATE (state) {
      state.lastDossierRefresh = new Date().getTime()
    },
    UPDATE_DOSSIER (state, result) {
      if (result.DisclaimerAccepted === false) {
        state.needsAcceptDisclaimer = true
        state.loggedIn = true
      } else if (result.Dossier) {
        state.Id = result.Dossier.Id
        state.loggedIn = true
      }
    },
    HANDLE_INTERNAL_LOGIN (state, result, user) {
      state.loggedIn = true
      state.internalUser = user
      state.Id = result
    },
    HANDLE_ACCEPT_TERMS (state) {
      state.needsAcceptDisclaimer = false
    },
    HANDLE_COUNTRIES (state, result) {
      state.countries = result || []
    },
    HANDLE_SUPPORTED_FILETYPES (state, result) {
      state.supportedFileTypes = result || []
    },
    HANDLE_LOGOUT (state) {
      sessionStorage.clear()
      state.loggedIn = false
      state.Id = null
    },
    SET_POST_LOGIN_REDIRECT (state, data) {
      state.postLoginRedirect = data
    },
    CHECK_LOGIN (state, data) {
      if (!data.loggedin) {
        sessionStorage.clear()
        state.loggedIn = false
      }
      state.loggedIn = data.loggedin
    }
  },
  getters: {
    getVersion () {
      return '1.14'
    },
    getCountries: state => state.countries,
    getCountryName: (state) => (CountryCode) => {
      const cntrs = state.countries.filter(el => el.Code === CountryCode)
      return cntrs.length === 1 ? cntrs[0].Name : ''
    },
    getCulture (state) {
      return state.culture.toLowerCase() || cultureCheck
    },
    getSupportedFileTypes: state => {
      return state.supportedFileTypes
    },
    getPostLoginRedirect: state => {
      if (state.needsAcceptDisclaimer) {
        return false
      } else {
        if (state.culture === 'de-DE') {
          return `/mein-euclaim#${state.postLoginRedirect || '/'}`
        } else if (state.culture === 'en-GB') {
          return `/my-euclaim#${state.postLoginRedirect || '/'}`
        }
        return `/mijn-euclaim#${state.postLoginRedirect || '/'}`
      }
    },
    getLogin (state) {
      return state.loggedIn
    },
    getDisclaimerState: state => {
      return state.needsAcceptDisclaimer
    }
  }
})
